import { useEffect, useState, type FC, type ReactElement, type ReactNode } from "react";
import { Link } from "react-router-dom";

import { IconClock } from "@/assets/icons/geist/IconClock";
import { IconInfo } from "@/assets/icons/geist/IconInfo";
import { IconRefresh } from "@/assets/icons/geist/IconRefresh";
import {
    getHealthLevel,
    getMetricHealthLevel,
    getPressureMetrics,
    type HealthLevel,
    type ResourceMetricKey,
} from "@/components/environments/resource-usage-health";
import { ResourceUsageSkeleton } from "@/components/environments/ResourceUsageSkeleton";
import { Banner } from "@/components/flexkit/Banner";
import { Button } from "@/components/flexkit/Button";
import { Text } from "@/components/flexkit/typography/Text";
import { cn } from "@/components/podkit/lib/cn";
import { Tooltip } from "@/components/Tooltip";
import { formatBytes } from "@/format/bytes";
import { timeAgo } from "@/format/time";
import type { DiskIORate, VMSnapshot } from "@/hooks/use-vm-live-usage";

// --- Resource Usage Content (used by ResourceUsageDialog) ---

export type ResourceUsageContentProps = {
    data: VMSnapshot | undefined;
    diskIORate: DiskIORate | undefined;
    error: string | undefined;
    isLoading: boolean;
    lastUpdated: Date | undefined;
    onRefresh?: () => void;
    projectId?: string;
};

export const ResourceUsageContent: FC<ResourceUsageContentProps> = ({
    data,
    diskIORate,
    error,
    isLoading,
    lastUpdated,
    onRefresh,
    projectId,
}) => {
    const healthLevel = data ? getHealthLevel(data) : "unknown";
    const bannerMessage =
        data && (healthLevel === "medium" || healthLevel === "high")
            ? getResourceBannerMessage(data, projectId)
            : undefined;

    return (
        <div data-testid="resource-usage-panel" className="flex flex-col gap-5">
            {bannerMessage && <Banner variant={healthLevel === "high" ? "danger" : "warning"} text={bannerMessage} />}
            {data && <ResourceStats snapshot={data} diskIORate={diskIORate} />}
            {!data && !error && isLoading && <ResourceUsageSkeleton />}
            {!data && error && (
                <div className="flex flex-col items-center gap-3 py-8 text-center">
                    <Text className="text-base font-medium text-content-primary">
                        Couldn&apos;t load resource usage
                    </Text>
                    <Text className="text-sm text-content-secondary">
                        Try again, or contact support if the issue persists.
                    </Text>
                    {onRefresh && (
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={onRefresh}
                            disabled={isLoading}
                            data-tracking-id="resource-usage-retry"
                            LeadingIcon={(props) => (
                                <IconRefresh {...props} className={cn(props.className, isLoading && "animate-spin")} />
                            )}
                        >
                            Retry
                        </Button>
                    )}
                </div>
            )}
            <ResourceUsageFooter lastUpdated={lastUpdated} isLoading={isLoading} onRefresh={onRefresh} />
            {data && error && (
                <Banner
                    variant="danger"
                    text="Couldn't refresh data. Try again, or contact support if the issue persists."
                    action={
                        onRefresh
                            ? {
                                  text: "Retry",
                                  onClick: onRefresh,
                                  disabled: isLoading,
                                  "data-tracking-id": "resource-usage-retry",
                              }
                            : undefined
                    }
                />
            )}
        </div>
    );
};

// --- Sub-components ---

type ResourceUsageFooterProps = {
    lastUpdated: Date | undefined;
    isLoading: boolean;
    onRefresh?: () => void;
};

const ResourceUsageFooter: FC<ResourceUsageFooterProps> = ({ lastUpdated, isLoading, onRefresh }) => {
    const [, setTick] = useState(0);

    // Re-render every 10s to keep the relative timestamp fresh
    useEffect(() => {
        const interval = setInterval(() => setTick((t) => t + 1), 10_000);
        return () => clearInterval(interval);
    }, []);

    if (!lastUpdated) {
        return null;
    }

    const label = `Updated ${Date.now() - lastUpdated.getTime() < 5000 ? "just now" : timeAgo(lastUpdated)}`;

    return (
        <div className="flex items-center justify-between pt-2">
            <div className="flex items-center gap-1.5">
                <IconClock size="sm" className="text-content-tertiary" style={{ marginTop: -1 }} aria-hidden />
                <Text className="text-sm text-content-tertiary">{label}</Text>
            </div>
            {onRefresh && (
                <Button
                    variant="secondary"
                    size="sm"
                    onClick={onRefresh}
                    disabled={isLoading}
                    data-tracking-id="resource-usage-refresh"
                    LeadingIcon={(props) => (
                        <IconRefresh {...props} className={cn(props.className, isLoading && "animate-spin")} />
                    )}
                >
                    Refresh
                </Button>
            )}
        </div>
    );
};

const ResourceStats: FC<{ snapshot: VMSnapshot; diskIORate: DiskIORate | undefined }> = ({ snapshot, diskIORate }) => {
    const { vm } = snapshot;
    const hasDualDisk = !!vm.data_disk;

    const memPercent = vm.memory.total_bytes > 0 ? (vm.memory.used_bytes / vm.memory.total_bytes) * 100 : 0;
    const diskPercent = vm.disk.total_bytes > 0 ? (vm.disk.used_bytes / vm.disk.total_bytes) * 100 : 0;

    return (
        <div className="flex flex-col gap-5">
            <UsageBar label="CPU" percent={vm.cpu.usage_percent} />

            <UsageBar
                label="Memory"
                percent={memPercent}
                detail={`${formatBytes(vm.memory.used_bytes)} / ${formatBytes(vm.memory.total_bytes)}`}
            />

            <UsageBar
                label={hasDualDisk ? "System Disk" : "Disk"}
                percent={diskPercent}
                detail={`${formatBytes(vm.disk.used_bytes)} / ${formatBytes(vm.disk.total_bytes)}`}
            />

            {vm.data_disk && (
                <UsageBar
                    label="Data Disk"
                    percent={
                        vm.data_disk.total_bytes > 0 ? (vm.data_disk.used_bytes / vm.data_disk.total_bytes) * 100 : 0
                    }
                    detail={`${formatBytes(vm.data_disk.used_bytes)} / ${formatBytes(vm.data_disk.total_bytes)}`}
                />
            )}

            <div className="flex flex-col gap-3">
                <DiskIORow
                    label="Disk Read"
                    bytesPerSec={diskIORate?.readBytesPerSec}
                    opsPerSec={diskIORate?.readOpsPerSec}
                />
                <DiskIORow
                    label="Disk Write"
                    bytesPerSec={diskIORate?.writeBytesPerSec}
                    opsPerSec={diskIORate?.writeOpsPerSec}
                />
            </div>

            <div className="border-t border-border-subtle pt-5">
                <StatRow
                    label="Load average (1m / 5m / 15m)"
                    tooltip="Average number of processes waiting to run over 1, 5, and 15 minute windows. Values above the core count indicate saturation."
                >
                    <span className="text-sm text-content-tertiary">{vm.cpu.cores} cores</span>
                    <span className="font-mono text-base text-content-primary">
                        {vm.load.avg1.toFixed(2)} / {vm.load.avg5.toFixed(2)} / {vm.load.avg15.toFixed(2)}
                    </span>
                </StatRow>
            </div>
        </div>
    );
};

const diskIOTooltips: Record<string, string> = {
    "Disk Read": "Data read from disk per second. IOPS = individual read operations per second.",
    "Disk Write": "Data written to disk per second. IOPS = individual write operations per second.",
};

const DiskIORow: FC<{ label: string; bytesPerSec: number | undefined; opsPerSec: number | undefined }> = ({
    label,
    bytesPerSec,
    opsPerSec,
}) => (
    <StatRow label={label} tooltip={diskIOTooltips[label]}>
        {bytesPerSec != null && opsPerSec != null ? (
            <>
                <span className="text-sm text-content-tertiary">{Math.round(opsPerSec)} iops</span>
                <span className="font-mono text-base text-content-primary">{formatBytes(bytesPerSec)}/s</span>
            </>
        ) : (
            <>
                <span className="text-sm text-content-tertiary">Refresh to update</span>
                <span className="font-mono text-base text-content-primary">N/A</span>
            </>
        )}
    </StatRow>
);

// --- Primitives ---

const StatRow: FC<{ label: string; tooltip?: string; children: ReactNode }> = ({ label, tooltip, children }) => (
    <div className="flex flex-wrap items-baseline justify-between gap-x-2">
        <div className="flex items-center gap-1">
            <Text className="text-base text-content-strong">{label}</Text>
            {tooltip && (
                <Tooltip content={tooltip} usePortal className="!m-0 max-w-xs">
                    <span className="inline-flex" style={{ marginTop: -1 }}>
                        <IconInfo size="sm" className="size-3.5 text-content-tertiary" aria-hidden />
                    </span>
                </Tooltip>
            )}
        </div>
        <div className="flex flex-wrap items-baseline gap-x-1.5">{children}</div>
    </div>
);

const MEDIUM_MESSAGES: Record<ResourceMetricKey, { headline: string; detail: string; action: string }> = {
    cpu: {
        headline: "CPU usage is elevated.",
        detail: "Builds and background tasks may take longer.",
        action: "A larger environment can help maintain performance.",
    },
    memory: {
        headline: "Memory usage is elevated.",
        detail: "The editor or running processes may feel less responsive.",
        action: "A larger environment can help maintain performance.",
    },
    disk: {
        headline: "Disk usage is elevated.",
        detail: "Git operations and builds may take longer.",
        action: "A larger environment provides additional storage capacity.",
    },
};

const HIGH_MESSAGES: Record<ResourceMetricKey, { headline: string; detail: string; action: string }> = {
    cpu: {
        headline: "CPU is nearing capacity.",
        detail: "Builds and tasks may stall or be interrupted.",
        action: "Switch to a larger environment to maintain reliable performance.",
    },
    memory: {
        headline: "Memory is nearing capacity.",
        detail: "Processes may be terminated as limits are reached.",
        action: "Switch to a larger environment to prevent interruptions.",
    },
    disk: {
        headline: "Disk is nearly full.",
        detail: "Writes may fail and builds may stop.",
        action: "Switch to a larger environment for additional storage.",
    },
};

function getResourceBannerMessage(data: VMSnapshot, projectId?: string): ReactElement {
    const metrics = getPressureMetrics(data);
    const highest = metrics.reduce((a, b) => (b.percent > a.percent ? b : a));
    const messages = highest.level === "high" ? HIGH_MESSAGES : MEDIUM_MESSAGES;
    const { headline, detail, action } = messages[highest.key];
    return (
        <span>
            <span className="font-medium">{headline}</span>
            <br />
            {detail} {action}
            {projectId && (
                <>
                    {" "}
                    <Link
                        to={`/projects/${projectId}/settings`}
                        className="underline"
                        data-tracking-id="resource-usage-project-settings"
                    >
                        Project settings
                    </Link>
                </>
            )}
        </span>
    );
}

function barFillClass(level: Exclude<HealthLevel, "unknown">): string {
    const colors: Record<Exclude<HealthLevel, "unknown">, string> = {
        low: "bg-content-brand",
        medium: "bg-[rgb(var(--ona-orange-300))]",
        high: "bg-[rgb(var(--ona-red-300))]",
    };
    return cn("h-full rounded-full transition-all duration-500", colors[level]);
}

const UsageBar: FC<{ label: string; percent: number; detail?: string }> = ({ label, percent, detail }) => {
    const clamped = Math.min(100, Math.max(0, percent));
    const metricLevel = getMetricHealthLevel(clamped);

    return (
        <div className="flex flex-col gap-1.5">
            <div className="flex flex-wrap items-baseline justify-between gap-x-2">
                <Text className="text-base text-content-strong">{label}</Text>
                <div className="flex flex-wrap items-baseline gap-x-2">
                    {detail && <span className="font-mono text-sm text-content-tertiary">{detail}</span>}
                    <span className="font-mono text-base text-content-primary">{clamped.toFixed(1)}%</span>
                </div>
            </div>
            <div className="h-2.5 w-full overflow-hidden rounded-full border border-border-subtle bg-surface-base">
                <div className={barFillClass(metricLevel)} style={{ width: `${clamped}%` }} />
            </div>
        </div>
    );
};
